import { Flex, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '../../../components/overview-card/overview-card';
import { ProgressBarCard } from '../../../components/overview-card/progress-bar-card';
import { formatCurrency, getCurrency } from '../../../helpers/currency-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { graphCambridgeBlue, purpleBase, skyDark } from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';
import { formatInitiativeDate } from '../strategy.helpers';
import { getCompletedTasksCount } from './initiative-performance.helpers';

export const InitiativePerformanceOverview = () => {
  const i18n = useTranslation();
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  return (
    <Flex direction="row" gap={24}>
      <ProgressBarCard
        title={i18n.t('strategy.initiative_performance.initiative_completion')}
        icon="check_circle"
        status={
          <Flex direction="column">
            <Flex justify="space-between">
              <Text size="tiny" color={skyDark}>
                {i18n.t('strategy.initiative_performance.start_date')}
              </Text>
              <Text size="tiny">{formatInitiativeDate(initiativePerformance?.start_date, i18n.t)}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text size="tiny" color={skyDark}>
                {i18n.t('strategy.initiative_performance.end_date')}
              </Text>
              <Text size="tiny">{formatInitiativeDate(initiativePerformance?.end_date, i18n.t)}</Text>
            </Flex>
            {initiativePerformance?.effective_start_date &&
              initiativePerformance?.start_date !== initiativePerformance?.effective_start_date && (
                <Flex justify="space-between">
                  <Text size="tiny" color={skyDark}>
                    {i18n.t('strategy.initiative_performance.activity_start_date')}
                  </Text>
                  <Text size="tiny">{formatInitiativeDate(initiativePerformance?.effective_start_date, i18n.t)}</Text>
                </Flex>
              )}
            {initiativePerformance?.effective_end_date &&
              initiativePerformance?.end_date !== initiativePerformance?.effective_end_date && (
                <Flex justify="space-between">
                  <Text size="tiny" color={skyDark}>
                    {i18n.t('strategy.initiative_performance.activity_end_date')}
                  </Text>
                  <Text size="tiny">{formatInitiativeDate(initiativePerformance?.effective_end_date, i18n.t)}</Text>
                </Flex>
              )}
          </Flex>
        }
        value={initiativePerformance?.percent_progress || 0}
        color={graphCambridgeBlue}
      />
      <ProgressBarCard
        title={i18n.t('strategy.initiative_performance.time_elapsed')}
        icon="check_circle"
        value={initiativePerformance?.percent_elapsed || 0}
        color={purpleBase}
      />
      <OverviewCard
        title={i18n.t('strategy.initiative_performance.cost_to_date')}
        icon="payments"
        content={
          <Flex align="center" justify="center" h="100%">
            <H3>{formatCurrency(initiativePerformance?.costs_to_date?.initiative || 0, { currency: currencyCode })}</H3>
          </Flex>
        }
      />
      <OverviewCard
        title={i18n.t('strategy.initiative_performance.throughput')}
        icon="show_chart"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{getCompletedTasksCount(initiativePerformance)}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {i18n.t('strategy.overview.tasks_completed', { work_units: i18n.t('common.work_units') })}
            </Text>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {i18n.t('strategy.overview.all_time')}
            </Text>
          </Stack>
        }
      />
    </Flex>
  );
};
